import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { bookingsActions } from '.';
import { ApiError } from '../../models/common/api-error';
import { filter, of, takeUntil } from 'rxjs';
import { BookingsService } from '../../services/bookings.service';
import { storeAuthKey, storeBookingsKey } from '../reducers';
import {
  BookingsDisplayMode,
  BookingsScope,
  BookingsState,
} from './bookings.state';
import {
  endOfDay,
  endOfWeek,
  parseISO,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { DatetimeHelper } from '../../helpers/datetime.helper';
import { BookingStatus } from '../../models/entities/booking';
import { AuthState } from '../auth/auth.state';
import { RoomsService } from '../../services/rooms.service';

@Injectable({
  providedIn: 'root',
})
export class BookingsEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private bookingsService: BookingsService,
    private roomsService: RoomsService,
  ) {}

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        bookingsActions.findAll,
        bookingsActions.setDisplayMode,
        bookingsActions.setCalendarDate,
        bookingsActions.setSearch,
        bookingsActions.setBookingsFilter,
        bookingsActions.setPage,
        bookingsActions.setRoomsFilter,
        bookingsActions.setOrder,
        bookingsActions.setDatesRange,
      ),
      withLatestFrom(this.store$),
      mergeMap(([action, store]) => {
        const scope = action.scope;
        const bookingsState: BookingsState = store[storeBookingsKey];
        let from = null;
        let to = null;
        let page = null;
        let search = null;
        let status = null;
        let order_by = null;
        let single_booking_per_group = false;
        let roomIds = bookingsState[action.scope].roomsFilter.ids;
        const displayMode = bookingsState[scope].displayMode;

        switch (displayMode) {
          case BookingsDisplayMode.Week:
            from = DatetimeHelper.dateToApiFormat(
              startOfWeek(bookingsState[scope].calendarDate, {
                weekStartsOn: 1,
              }),
              true,
            );
            to = DatetimeHelper.dateToApiFormat(
              endOfWeek(bookingsState[scope].calendarDate, {
                weekStartsOn: 1,
              }),
              true,
            );
            // Pas de tri en mode Week
            break;

          case BookingsDisplayMode.Day:
            from = DatetimeHelper.dateToApiFormat(
              startOfDay(bookingsState[scope].calendarDate),
              true,
            );
            to = DatetimeHelper.dateToApiFormat(
              endOfDay(bookingsState[scope].calendarDate),
              true,
            );
            // Pas de tri en mode Day
            break;

          case BookingsDisplayMode.List:
            page = bookingsState[scope].page;
            search = bookingsState[scope].search;
            from = bookingsState[scope].from
              ? DatetimeHelper.dateToApiFormat(
                  startOfDay(parseISO(bookingsState[scope].from)),
                  true,
                )
              : null;
            to = bookingsState[scope].to
              ? DatetimeHelper.dateToApiFormat(
                  endOfDay(parseISO(bookingsState[scope].to)),
                  true,
                )
              : null;
            // Le tri n'est appliqué qu'en mode Liste
            order_by = bookingsState[scope].order;
            single_booking_per_group = true;
            break;
        }
        let user_id = null;
        if (scope === BookingsScope.Personal) {
          const authState: AuthState = store[storeAuthKey];
          user_id = authState.user?.id;
        }
        const nextSearch$ = this.actions$.pipe(
          ofType(
            bookingsActions.findAll,
            bookingsActions.setDisplayMode,
            bookingsActions.setCalendarDate,
            bookingsActions.setSearch,
            bookingsActions.setBookingsFilter,
            bookingsActions.setPage,
            bookingsActions.setRoomsFilter,
            bookingsActions.setOrder,
            bookingsActions.setDatesRange,
          ),
          filter((action) => action.scope === scope),
        );

        status = bookingsState[scope].bookingsFilter.status;

        return this.bookingsService
          .findAll({
            page,
            from,
            to,
            search,
            status,
            user_id,
            room_ids: roomIds,
            order_by: order_by?.map((orderBy) => [
              orderBy.field,
              orderBy.order,
            ]),
            minimum_data: true,
            single_booking_per_group,
          })
          .pipe(
            takeUntil(nextSearch$),
            map((response) =>
              bookingsActions.findAllSuccess({
                bookings: response.data,
                total: response.total,
                scope,
              }),
            ),
            catchError((error: ApiError) =>
              of(bookingsActions.findAllError({ error: error.message, scope })),
            ),
          );
      }),
    ),
  );

  findAllPending$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingsActions.findAllPending),
      switchMap(() => {
        return this.bookingsService
          .findAll({
            page: 1,
            status: [BookingStatus.Pending],
            single_booking_per_group: true,
          })
          .pipe(
            map((response) =>
              bookingsActions.findAllPendingSuccess({
                bookings: response.data,
                total: response.total,
              }),
            ),
            catchError((error: ApiError) =>
              of(bookingsActions.findAllPendingError({ error: error.message })),
            ),
          );
      }),
    ),
  );
}
